<script setup>
import {ref, computed, reactive} from "vue";
import Card from "~/components/ui/Card.vue";
import Button from "~/components/ui/Button.vue";
import CalendarIcon from "~/components/public/CalendarIcon.vue";
import {publicFetch} from "~/composables/publicFetch.js";
import NextMeetingCard from "~/components/meetings/NextMeetingCard.vue";
import Pagination from "~/components/public/Pagination.vue";
import Heading1 from "~/components/public/Heading1.vue";
import MeetingFilters from "~/components/meetings/MeetingFilters.vue";

const route = useRoute();
const publicSiteData = useState('publicSiteData');

const search = ref('');
const selectedGroup = ref('');
const selectedStatus = ref('');
const selectedSort = ref('asc');
const count = ref(10);
const tooltipStates = reactive({});
const upcoming = ref(true);

// Fetch initial data
const { data: initialData, refresh } = await useAsyncData(
    'meetings-data',
    async () => {
        if (!publicSiteData.value?.id) {
            return {
                meetings: [],
                pagination: {},
                groups: []
            };
        }

        const [meetingsResponse, groupsResponse] = await Promise.all([
            publicFetch('/public-sites/meetings', {
                query: {
                    count: count.value,
                    search: search.value,
                    group: selectedGroup.value,
                    status: selectedStatus.value,
                    sort: selectedSort.value,
                    page: 1,
                    upcoming: upcoming.value
                }
            }).catch(e => {
                return { data: [], meta: {}, links: {} };
            }),
            publicFetch('/public-sites/meeting-filter-groups')
                .catch(() => ({ groups: [] }))
        ]);

        const result = {
            meetings: meetingsResponse.data?.map(meeting => ({
                id: meeting.id,
                title: meeting.title,
                slug: meeting.slug,
                start: meeting.formattedMeetingTime.start.date,
                dateDisplay: meeting.formattedMeetingTime.datetime || '',
                groups: meeting.groups || [],
                materialsPublished: !!meeting.published_materials_status,
                materialsPublishedDate: meeting.formattedMeetingTime.materials_published_at || ''
            })) || [],
            pagination: { ...meetingsResponse.meta, ...meetingsResponse.links },
            groups: groupsResponse.groups?.map(group => group.name) || []
        };

        return result;
    },
    {
        lazy: false
    }
);

// Use refs that get updated
const meetings = ref(initialData.value?.meetings || []);
const allGroups = ref(initialData.value?.groups || []);
const pagination = ref(initialData.value?.pagination || {});
const loading = ref(false);

// Watch for data updates
watch(initialData, (newData) => {
    if (newData) {
        meetings.value = newData.meetings || [];
        allGroups.value = newData.groups || [];
        pagination.value = newData.pagination || {};
    }
});

const groupOptions = computed(() => {
    return allGroups.value.map(group => ({
        value: group,
        label: group
    }));
});

const fetchMeetings = async (page = 1) => {
    loading.value = true;
    try {
        const response = await publicFetch('/public-sites/meetings', {
            query: {
                count: count.value,
                search: search.value,
                group: selectedGroup.value,
                status: selectedStatus.value,
                sort: selectedSort.value,
                page: page,
                upcoming: upcoming.value
            }
        });

        meetings.value = response.data.map(meeting => ({
            id: meeting.id,
            title: meeting.title,
            slug: meeting.slug,
            start: meeting.formattedMeetingTime.start.date,
            dateDisplay: meeting.formattedMeetingTime.datetime || '',
            groups: meeting.groups || [],
            materialsPublished: !!meeting.published_materials_status,
            materialsPublishedDate: meeting.formattedMeetingTime.materials_published_at || ''
        }));

        pagination.value = {...response.meta, ...response.links};
    }
    catch (e) {
        console.log(e);
    }
    finally {
        loading.value = false;
    }
};

watch([search, selectedGroup, selectedStatus, selectedSort, upcoming],
    ([newSearch, newGroup, newStatus, newSort, newUpcoming],
     [oldSearch, oldGroup, oldStatus, oldSort, oldUpcoming]) => {
        if (newUpcoming !== oldUpcoming) {
            if(!newUpcoming){
                selectedSort.value = ''
            } else {
                selectedSort.value = 'asc'
            }
        }
        fetchMeetings();
    });
</script>

<template>
    <Title>{{ publicSiteData?.organization_display_name ? `Meetings: ${publicSiteData.organization_display_name}` : 'Meetings' }}</Title>

    <ClientOnly>
        <div v-if="publicSiteData?.next_meeting">
            <Heading1 class="mb-4">Next meeting</Heading1>
            <NextMeetingCard :meeting="publicSiteData.next_meeting" class="mb-8" />
        </div>
    </ClientOnly>

    <div class="flex border-b border-gray-300 mb-6">
        <p class="mr-6 cursor-pointer text-gray-700 hover:text-gray-900" :class="{ 'text-primary border-b border-primary pb-3 hover:text-primary': upcoming === true }" @click.prevent="upcoming = true">Upcoming</p>
        <p class="cursor-pointer text-gray-700 hover:text-gray-900" :class="{ 'text-primary border-b border-primary pb-3 hover:text-primary': upcoming === false }" @click.prevent="upcoming = false">Past</p>
    </div>

    <Card>
        <template #header>
            <MeetingFilters
                v-model:search="search"
                v-model:selected-group="selectedGroup"
                v-model:selected-status="selectedStatus"
                v-model:selected-sort="selectedSort"
                :group-options="groupOptions"
                :public-side="true"
            />
        </template>

        <div v-if="loading" class="flex items-center justify-center h-32">
            <font-awesome :icon="['fas', 'spinner']" class="animate-spin text-primary h-8 w-8" />
        </div>

        <div v-else-if="meetings.length > 0">
            <template v-for="meeting in meetings" :key="meeting.id">
                <div class="py-4 border-b border-gray-200 last:border-b-0">
                    <div class="flex justify-between items-center">
                        <div class="flex items-center gap-4">
                            <CalendarIcon :date="meeting.start" size="large" />
                            <div>
                                <NuxtLink :to="`/${route.params.site_slug}/meetings/${meeting.slug}`" class="hover:cursor-pointer">
                                    <h3 class="font-medium">{{ meeting.title }}</h3>
                                </NuxtLink>
                                <div class="text-sm text-gray-700">{{ meeting.dateDisplay }}</div>
                            </div>
                        </div>

                        <div class="flex items-center gap-3">
                            <span v-if="meeting.groups?.length > 2">
                                <span class="hidden sm:inline-block text-sm bg-gray-100 border border-gray-300 text-gray-700 rounded px-2 py-1 relative cursor-pointer mr-2">
                                    <font-awesome class="w-4 h-4 mr-1" :icon="['fas', 'user-group']" />
                                    {{ meeting.groups[0].name }}
                                </span>
                                <span
                                    class="hidden sm:inline-block text-sm bg-gray-100 border border-gray-300 text-gray-700 rounded px-2 py-1 relative cursor-pointer"
                                    @mouseenter="$set(tooltipStates, meeting.id, true)"
                                    @mouseleave="$set(tooltipStates, meeting.id, false)">
                                    <font-awesome class="w-4 h-4 mr-1" :icon="['fas', 'user-group']" />
                                    +{{ meeting.groups.length - 1 }}
                                    <ClientOnly>
                                        <div v-if="tooltipStates[meeting.id]" class="absolute top-full mt-1 left-0 bg-gray-800 text-white text-xs rounded px-2 py-1 whitespace-pre z-10">
                                            {{ meeting.groups.slice(1).map(g => g.name).join('\n') }}
                                        </div>
                                    </ClientOnly>
                                </span>
                            </span>
                            <template v-else>
                                <span
                                    v-for="group in meeting.groups"
                                    :key="group.id"
                                    class="hidden sm:inline-block text-sm bg-gray-100 border border-gray-300 text-gray-700 rounded px-2 py-1">
                                    <font-awesome class="w-4 h-4 mr-1" :icon="['fas', 'user-group']" />
                                    {{ group.name }}
                                </span>
                            </template>

                            <NuxtLink v-if="meeting.materialsPublished" :to="`/${route.params.site_slug}/meetings/${meeting.slug}`" class="hidden sm:inline-block p-2 flex items-center justify-center gap-1 border-gray-400 hover:bg-gray-200 text-black bg-white border rounded-md px-2 py-1 text-sm min-h-[32px] min-w-[42px]">
                                View agenda & details
                                <font-awesome class="h-3.5 w-3.5 ml-1" :icon="['far', 'chevron-right']" />
                            </NuxtLink>
                        </div>
                    </div>
                </div>
            </template>
        </div>

        <div v-else class="text-center py-12">
            <font-awesome :icon="['far', 'calendar']" class="h-12 w-12 text-gray-400 mb-4" />
            <div class="text-gray-700 font-light">
                <span v-if="search">We couldn't find anything matching your search.</span>
                <span v-else>No meetings have been published yet.</span>
            </div>
        </div>

        <template #footer v-if="pagination && pagination.total > pagination.per_page">
            <Pagination :pagination="pagination" @page-change="fetchMeetings" />
        </template>
    </Card>
</template>